import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { COMPANY_URL } from "@/lib/brand";
import { toast } from "sonner";

type Review = { id: string; rating: number; comment: string | null; created_at: string; verified: boolean };
type Cert = { name: string; issuer: string | null; issued_on: string | null; url: string | null };
type Venue = { venue_name: string; role: string | null; city: string | null };
type Profile = {
  handle: string;
  full_name: string | null;
  headline: string | null;
  about_me: string | null;
  specialties: string[] | null;
  years_experience: number | null;
  avatar_path: string | null;
  is_verified: boolean;
  is_mixologist: boolean;
  has_dbs: boolean;
  rtw_status: string;
  rating_avg: number | null;
  rating_count: number | null;
  reviews: Review[];
  certifications: Cert[];
  venues_worked: Venue[];
};

async function loadProfile(handle: string): Promise<Profile | null> {
  const { data, error } = await supabase.rpc("get_public_bartender_profile", { _handle: handle });
  if (error || !data) return null;
  return data as unknown as Profile;
}

export const Route = createFileRoute("/b/$handle")({
  loader: async ({ params }) => {
    const profile = await loadProfile(params.handle);
    if (!profile) throw notFound();
    return { profile };
  },
  head: ({ loaderData }) => {
    const p = loaderData?.profile;
    const title = p ? `${p.full_name ?? p.handle} — Bartender on The Hospitality Reserve` : "Bartender profile";
    const desc = p?.headline
      ? p.headline
      : `Verified bartender profile with reviews and certifications on The Hospitality Reserve.`;
    const url = `${COMPANY_URL}/b/${p?.handle ?? ""}`;
    return {
      meta: [
        { title }, { name: "description", content: desc },
        { property: "og:title", content: title }, { property: "og:description", content: desc },
        { property: "og:url", content: url }, { property: "og:type", content: "profile" },
        { property: "og:site_name", content: "The Hospitality Reserve" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title }, { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PublicBartender,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center text-foreground">
      <div className="text-center"><p className="text-lg">Profile not found.</p>
        <Link to="/" className="underline text-accent">Back to home</Link></div>
    </div>
  ),
});

function Initials({ name }: { name: string | null }) {
  const v = (name ?? "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return <div className="h-24 w-24 rounded-full bg-accent/20 text-accent grid place-items-center text-2xl font-display">{v}</div>;
}

function Badge({ tone, children }: { tone: "ok" | "pending" | "muted" | "warn"; children: React.ReactNode }) {
  const styles =
    tone === "ok" ? "bg-emerald-500/10 text-emerald-600 border border-emerald-500/30"
    : tone === "pending" ? "bg-amber-500/10 text-amber-600 border border-amber-500/30"
    : tone === "warn" ? "bg-destructive/10 text-destructive border border-destructive/30"
    : "bg-muted text-muted-foreground border border-border";
  return <span className={`text-xs px-2 py-1 rounded-full ${styles}`}>{children}</span>;
}

function rtwLabel(status: string): { tone: "ok" | "pending" | "warn" | "muted"; label: string } {
  switch (status) {
    case "verified": return { tone: "ok", label: "Right to Work · Verified" };
    case "in_review": case "pending": return { tone: "pending", label: "Right to Work · In review" };
    case "rejected": return { tone: "warn", label: "Right to Work · Action needed" };
    case "expired": return { tone: "warn", label: "Right to Work · Expired" };
    default: return { tone: "muted", label: "Right to Work · Not submitted" };
  }
}

function ReviewItem({ review }: { review: Review }) {
  const [reporting, setReporting] = useState(false);
  async function report() {
    const reason = window.prompt("Tell us briefly why you're reporting this review:");
    if (!reason || reason.trim().length < 3) return;
    setReporting(true);
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) {
      toast.error("Please sign in to report a review.");
      setReporting(false); return;
    }
    const { error } = await supabase.rpc("report_review", { _review_id: review.id, _reason: reason.trim() });
    setReporting(false);
    if (error) toast.error(error.message); else toast.success("Thanks — our moderation team will review it.");
  }
  return (
    <li className="border border-border p-3 rounded">
      <div className="flex items-center justify-between">
        <div className="text-sm">★ {review.rating}</div>
        {review.verified && <Badge tone="ok">Verified shift</Badge>}
      </div>
      {review.comment && <p className="mt-1 leading-relaxed">{review.comment}</p>}
      <div className="text-xs text-muted-foreground mt-1 flex items-center justify-between">
        <span>{new Date(review.created_at).toLocaleDateString()}</span>
        <button onClick={report} disabled={reporting} className="underline hover:text-foreground">
          {reporting ? "Reporting…" : "Report"}
        </button>
      </div>
    </li>
  );
}

function PublicBartender() {
  const { profile: p } = Route.useLoaderData() as { profile: Profile };
  const rtw = rtwLabel(p.rtw_status);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: p.full_name ?? p.handle,
    description: p.headline ?? p.about_me ?? undefined,
    jobTitle: p.is_mixologist ? "Verified Mixologist" : "Bartender",
    aggregateRating: p.rating_count && p.rating_avg ? {
      "@type": "AggregateRating", ratingValue: p.rating_avg, reviewCount: p.rating_count,
    } : undefined,
  };
  return (
    <div className="min-h-screen bg-background text-foreground">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c") }} />
      <header className="px-6 py-5 border-b border-border flex items-center justify-between">
        <Link to="/" className="font-display text-foreground">The Hospitality Reserve</Link>
        <Link to="/auth" className="text-xs tracking-wide-luxury text-accent">Hire bartenders</Link>
      </header>

      <section className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-start gap-6">
          <Initials name={p.full_name} />
          <div className="flex-1">
            <h1 className="font-display text-3xl">{p.full_name ?? p.handle}</h1>
            {p.headline && <p className="text-muted-foreground mt-1">{p.headline}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              {p.is_verified && <Badge tone="ok">Identity verified</Badge>}
              {p.is_mixologist && <Badge tone="ok">Verified Mixologist</Badge>}
              {p.has_dbs ? <Badge tone="ok">DBS on file</Badge> : <Badge tone="muted">DBS not on file</Badge>}
              <Badge tone={rtw.tone}>{rtw.label}</Badge>
              {typeof p.years_experience === "number" && (
                <Badge tone="muted">{p.years_experience}+ yrs</Badge>
              )}
              {p.rating_count ? <Badge tone="muted">★ {p.rating_avg} ({p.rating_count})</Badge> : null}
            </div>
          </div>
        </div>

        {p.about_me && (
          <div className="mt-8"><h2 className="text-sm tracking-wide-luxury text-muted-foreground mb-2">About</h2>
            <p className="leading-relaxed whitespace-pre-wrap">{p.about_me}</p></div>
        )}

        {p.specialties?.length ? (
          <div className="mt-8"><h2 className="text-sm tracking-wide-luxury text-muted-foreground mb-2">Specialties</h2>
            <div className="flex flex-wrap gap-2">{p.specialties.map((s) => (
              <span key={s} className="text-xs px-2 py-1 rounded-full border border-border">{s}</span>))}</div></div>
        ) : null}

        <div className="mt-8">
          <h2 className="text-sm tracking-wide-luxury text-muted-foreground mb-2">Certifications</h2>
          {p.certifications.length === 0 ? (
            <p className="text-muted-foreground text-sm">No certifications added yet.</p>
          ) : (
            <ul className="space-y-2">{p.certifications.map((c, i) => (
              <li key={i} className="flex justify-between border-b border-border py-2">
                <span>
                  {c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" className="underline">{c.name}</a> : c.name}
                  {c.issuer ? <span className="text-muted-foreground"> · {c.issuer}</span> : null}
                </span>
                <span className="text-muted-foreground text-sm">{c.issued_on ?? "—"}</span>
              </li>))}</ul>
          )}
        </div>

        {p.venues_worked.length > 0 && (
          <div className="mt-8"><h2 className="text-sm tracking-wide-luxury text-muted-foreground mb-2">Venues worked</h2>
            <ul className="space-y-1">{p.venues_worked.map((v, i) => (
              <li key={i}>{v.venue_name}{v.role ? <span className="text-muted-foreground"> · {v.role}</span> : null}{v.city ? <span className="text-muted-foreground"> · {v.city}</span> : null}</li>))}</ul></div>
        )}

        <div className="mt-8">
          <h2 className="text-sm tracking-wide-luxury text-muted-foreground mb-2">Reviews</h2>
          {p.reviews.length === 0 ? (
            <p className="text-muted-foreground text-sm">No reviews yet.</p>
          ) : (
            <ul className="space-y-3">{p.reviews.map((r) => <ReviewItem key={r.id} review={r} />)}</ul>
          )}
        </div>
      </section>
    </div>
  );
}
